import React, { useState } from "react";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { CalendarIcon, ChevronDown } from "lucide-react";

const fmt = (d) => {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const fmtPretty = (d) => {
  if (!d) return "";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const startOfWeek = (d) => {
  const x = new Date(d);
  const day = x.getDay(); // 0=Sun..6=Sat — we want Mon as first day
  const diff = (day === 0 ? -6 : 1 - day);
  x.setDate(x.getDate() + diff);
  x.setHours(0, 0, 0, 0);
  return x;
};

const PRESETS = [
  { id: "today",    label: "Today",       getRange: () => { const t = new Date(); t.setHours(0,0,0,0); return { from: t, to: t }; } },
  { id: "yest",     label: "Yesterday",   getRange: () => { const y = new Date(); y.setDate(y.getDate() - 1); y.setHours(0,0,0,0); return { from: y, to: y }; } },
  { id: "last7",    label: "Last 7 days", getRange: () => { const t = new Date(); const f = new Date(); f.setDate(f.getDate() - 6); return { from: f, to: t }; } },
  { id: "this_wk",  label: "This week",   getRange: () => { const t = new Date(); return { from: startOfWeek(t), to: t }; } },
  { id: "last_wk",  label: "Last week",   getRange: () => { const t = startOfWeek(new Date()); const from = new Date(t); from.setDate(from.getDate() - 7); const to = new Date(t); to.setDate(to.getDate() - 1); return { from, to }; } },
  { id: "last30",   label: "Last 30 days", getRange: () => { const t = new Date(); const f = new Date(); f.setDate(f.getDate() - 29); return { from: f, to: t }; } },
  { id: "this_mo",  label: "This month",  getRange: () => { const t = new Date(); return { from: new Date(t.getFullYear(), t.getMonth(), 1), to: t }; } },
  { id: "last_mo",  label: "Last month",  getRange: () => { const t = new Date(); const from = new Date(t.getFullYear(), t.getMonth() - 1, 1); const to = new Date(t.getFullYear(), t.getMonth(), 0); return { from, to }; } },
  { id: "ytd",      label: "Year to date", getRange: () => { const t = new Date(); return { from: new Date(t.getFullYear(), 0, 1), to: t }; } },
  { id: "lifetime", label: "Lifetime",    getRange: () => { return { from: new Date(2024, 0, 1), to: new Date() }; } },
];

export const DateRangePicker = ({ value, onChange, minDate, maxDate, align = "start", buttonClassName = "", testId = "date-range-picker" }) => {
  // value: { from: Date|null, to: Date|null } or { from: 'YYYY-MM-DD', to: 'YYYY-MM-DD' }
  const parseDate = (v) => {
    if (!v) return null;
    if (v instanceof Date) return v;
    const [y, m, d] = String(v).split("-").map(Number);
    return new Date(y, m - 1, d);
  };
  const initial = { from: parseDate(value?.from), to: parseDate(value?.to) };

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(initial); // {from, to}
  const [activePreset, setActivePreset] = useState(null);

  const handlePreset = (p) => {
    const range = p.getRange();
    setDraft(range);
    setActivePreset(p.id);
  };

  const handleApply = () => {
    if (draft.from && draft.to) {
      onChange?.({ from: fmt(draft.from), to: fmt(draft.to), fromDate: draft.from, toDate: draft.to });
      setOpen(false);
    }
  };

  const handleCancel = () => {
    setDraft(initial);
    setActivePreset(null);
    setOpen(false);
  };

  const buttonLabel = (() => {
    if (initial.from && initial.to) {
      if (fmt(initial.from) === fmt(initial.to)) return fmtPretty(initial.from);
      return `${fmtPretty(initial.from)}  →  ${fmtPretty(initial.to)}`;
    }
    return "Select date range";
  })();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-md hover:border-indigo-300 hover:bg-gray-50 transition-colors ${buttonClassName}`}
          data-testid={`${testId}-trigger`}
        >
          <CalendarIcon className="w-4 h-4 text-indigo-500" />
          <span className="tabular-nums">{buttonLabel}</span>
          <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align={align}
        className="p-0 w-auto"
        data-testid={`${testId}-popover`}
      >
        <div className="flex">
          {/* Presets sidebar */}
          <div className="w-40 border-r border-gray-100 py-2 bg-gray-50/40">
            {PRESETS.map((p) => (
              <button
                key={p.id}
                onClick={() => handlePreset(p)}
                className={`w-full text-left px-4 py-2 text-[13px] font-medium transition-colors border-l-2 ${
                  activePreset === p.id
                    ? "bg-indigo-50 text-indigo-700 border-indigo-500"
                    : "text-gray-700 border-transparent hover:bg-gray-100"
                }`}
                data-testid={`${testId}-preset-${p.id}`}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Calendar */}
          <div className="flex flex-col">
            <div className="p-3">
              <Calendar
                mode="range"
                numberOfMonths={2}
                weekStartsOn={1}
                selected={draft}
                onSelect={(range) => { setDraft(range || { from: null, to: null }); setActivePreset(null); }}
                defaultMonth={draft?.from || new Date()}
                fromDate={minDate ? parseDate(minDate) : undefined}
                toDate={maxDate ? parseDate(maxDate) : undefined}
              />
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50/30">
              <div className="text-[11px] text-gray-500 tabular-nums">
                {draft.from && draft.to
                  ? `${fmtPretty(draft.from)}  →  ${fmtPretty(draft.to)}`
                  : draft.from
                    ? `From ${fmtPretty(draft.from)} — pick end date`
                    : "Pick a start date"}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCancel}
                  className="px-4 py-1.5 text-[12px] font-medium text-gray-600 hover:text-gray-800"
                  data-testid={`${testId}-cancel`}
                >Cancel</button>
                <button
                  onClick={handleApply}
                  disabled={!draft.from || !draft.to}
                  className="px-4 py-1.5 text-[12px] font-semibold text-white bg-gray-900 rounded-md hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  data-testid={`${testId}-apply`}
                >Apply</button>
              </div>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default DateRangePicker;
